import { useState, useEffect, useMemo, useCallback } from "react";
import { format, differenceInDays, addYears, isBefore } from "date-fns";
import { CalendarIcon, Plus, Trash2, Bell, Gift, Edit2, RefreshCw, CalendarX, Users, MessageSquare, ExternalLink, UserPlus, X, ChevronDown, ChevronUp, ShoppingBag, CreditCard, Crown, Shield, AlertCircle, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validateEmail } from "@/lib/emailValidation";
import { InviteLinkSettings } from "@/components/InviteLinkSettings";

interface GiftReminder {
  id: string;
  event_name: string;
  recipient_name: string | null;
  event_date: string;
  message: string | null;
  reminder_days_before: number;
  is_recurring: boolean;
  reminder_email: string | null;
  reminder_phone: string | null;
  occasion: string | null;
}

// Unified item type for all upcoming events
type EventCategory = 'gift' | 'purchase' | 'payment';

interface UnifiedEvent {
  id: string;
  category: EventCategory;
  title: string;
  recipient: string | null;
  date: Date;
  isRecurring: boolean;
  originalData: GiftReminder | GroupPurchase;
  role?: 'organizer' | 'admin' | 'participant';
}

interface GroupPurchase {
  id: string;
  title: string;
  organizer_name: string | null;
  beneficiary_name: string | null;
  target_date: string | null;
  status: string;
  created_at: string;
  payment_type: string | null; // 'none' for purchases, 'organizer' or 'merchant' for payments
}

const CATEGORY_CONFIG: Record<EventCategory, { color: string; bgColor: string; icon: React.ReactNode; label: string }> = {
  gift: { 
    color: 'text-pink-600 dark:text-pink-400', 
    bgColor: 'bg-gradient-to-r from-pink-50 to-pink-100 dark:from-pink-950/30 dark:to-pink-900/20', 
    icon: <Gift className="w-3 h-3" />,
    label: 'Group Gift'
  },
  purchase: { 
    color: 'text-violet-600 dark:text-violet-400', 
    bgColor: 'bg-gradient-to-r from-violet-50 to-violet-100 dark:from-violet-950/30 dark:to-violet-900/20', 
    icon: <ShoppingBag className="w-3 h-3" />,
    label: 'Group Purchase'
  },
  payment: { 
    color: 'text-emerald-600 dark:text-emerald-400', 
    bgColor: 'bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20', 
    icon: <CreditCard className="w-3 h-3" />,
    label: 'Group Payment'
  },
};

interface InviteFriend {
  email: string;
  phone: string;
  phoneCountryCode: string;
}

interface InvitedPerson {
  id: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
}

// Ordered by most common occasions first, with "Other" at the end
const OCCASIONS = [
  { value: "birthday", label: "🎂 Birthday" },
  { value: "anniversary", label: "💍 Anniversary" },
  { value: "wedding", label: "👰 Wedding" },
  { value: "festival", label: "🪔 Festival" },
  { value: "baby_shower", label: "👶 Baby Shower" },
  { value: "graduation", label: "🎓 Graduation" },
  { value: "housewarming", label: "🏠 Housewarming" },
  { value: "retirement", label: "🎉 Retirement" },
  { value: "promotion", label: "🚀 Promotion" },
  { value: "other", label: "✨ Other" },
];

interface CustomerImportantDatesProps {
  customerEmail?: string | null;
  customerPhone?: string | null;
  compact?: boolean;
  dialogOpenProp?: boolean;
  onDialogClose?: () => void;
  preselectedDate?: Date;
}

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳" },
  { code: "+1", flag: "🇺🇸" },
  { code: "+44", flag: "🇬🇧" },
  { code: "+971", flag: "🇦🇪" },
];

export function CustomerImportantDates({ 
  customerEmail, 
  customerPhone, 
  compact = false,
  dialogOpenProp,
  onDialogClose,
  preselectedDate
}: CustomerImportantDatesProps) {
  const [reminders, setReminders] = useState<GiftReminder[]>([]);
  const [groupPurchases, setGroupPurchases] = useState<GroupPurchase[]>([]);
  const [purchaseRoles, setPurchaseRoles] = useState<Map<string, 'organizer' | 'admin' | 'participant'>>(new Map());
  const [giftRoles, setGiftRoles] = useState<Map<string, 'organizer' | 'admin' | 'participant'>>(new Map());
  const [unifiedEvents, setUnifiedEvents] = useState<UnifiedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Form state
  const [date, setDate] = useState<Date | undefined>(preselectedDate);
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [reminderDays, setReminderDays] = useState("7");
  const [isRecurring, setIsRecurring] = useState(true);
  const [sendEmailReminder, setSendEmailReminder] = useState(true);
  const [reminderPhone, setReminderPhone] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+91");
  const [occasion, setOccasion] = useState("");
  const [customOccasion, setCustomOccasion] = useState("");
  const [saving, setSaving] = useState(false);
  const [inviteFriend, setInviteFriend] = useState<InviteFriend>({
    email: "",
    phone: "",
    phoneCountryCode: "+91",
  });
  const [invitedPeople, setInvitedPeople] = useState<InvitedPerson[]>([]);
  
  // Validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  // Email validation state
  const [inviteEmailError, setInviteEmailError] = useState<string>("");
  
  // Unsaved changes warning
  const [showExitWarning, setShowExitWarning] = useState(false);
  
  // Invite link settings state
  const [inviteLinkEnabled, setInviteLinkEnabled] = useState(false);
  const [inviteLinkApprovalMode, setInviteLinkApprovalMode] = useState<"auto" | "manual">("auto");
  const [maxSelfInvites, setMaxSelfInvites] = useState<number | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return recipientName.trim() !== "" || 
           message.trim() !== "" || 
           occasion !== "" ||
           invitedPeople.length > 0 ||
           date !== undefined;
  }, [recipientName, message, occasion, invitedPeople.length, date]);

  // Real-time email validation
  const validateInviteEmailInput = useCallback((email: string): string => {
    if (!email.trim()) return "";
    const result = validateEmail(email.trim());
    return result.valid ? "" : (result.error || "Invalid email");
  }, []);

  const handleInviteEmailChange = (value: string) => {
    setInviteFriend(prev => ({ ...prev, email: value }));
    if (value.length > 3) {
      setInviteEmailError(validateInviteEmailInput(value));
    } else {
      setInviteEmailError("");
    }
  };

  const handleInviteEmailBlur = () => {
    if (inviteFriend.email.trim()) {
      setInviteEmailError(validateInviteEmailInput(inviteFriend.email));
    }
  };

  // Field validation helper
  const validateReminderField = (field: string, value: string | Date | undefined): string => {
    switch (field) {
      case 'occasion':
        return value ? '' : 'Please select an occasion type';
      case 'recipientName':
        return (value as string)?.trim() ? '' : 'Please enter who this is for';
      case 'date':
        return value ? '' : 'Please select a date';
      default:
        return '';
    }
  };

  const handleReminderFieldBlur = (field: string, value: string | Date | undefined) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateReminderField(field, value);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  // Inline error component
  const FieldError = ({ error }: { error?: string }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1.5 text-destructive mt-1.5">
        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs">{error}</span>
      </div>
    );
  };

  // Handle preselected date changes
  useEffect(() => {
    if (preselectedDate) {
      setDate(preselectedDate);
    }
  }, [preselectedDate]);

  // Handle external dialog open trigger
  useEffect(() => {
    if (dialogOpenProp) {
      setDialogOpen(true);
    }
  }, [dialogOpenProp]);

  useEffect(() => {
    if (customerEmail || customerPhone) {
      fetchAllEvents();
    }
  }, [customerEmail, customerPhone]);

  // Combine all events into unified list whenever data changes
  useEffect(() => {
    const events: UnifiedEvent[] = [];
    
    // Add gift reminders
    reminders.forEach(reminder => {
      const eventDate = new Date(reminder.event_date);
      let nextDate = eventDate;
      
      if (reminder.is_recurring) {
        const today = new Date();
        nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        if (isBefore(nextDate, today)) {
          nextDate = addYears(nextDate, 1);
        }
      }
      
      events.push({
        id: reminder.id,
        category: 'gift',
        title: reminder.event_name,
        recipient: reminder.recipient_name,
        date: nextDate,
        isRecurring: reminder.is_recurring,
        originalData: reminder,
        role: giftRoles.get(reminder.id),
      });
    });
    
    // Add group purchases and payments (those with target dates)
    groupPurchases.forEach(purchase => {
      if (purchase.target_date && purchase.status === 'active') {
        // Use explicit category field set during creation
        const category = (purchase as any).category === 'payment' ? 'payment' : 'purchase';
        events.push({
          id: purchase.id,
          category: category,
          title: purchase.title,
          recipient: purchase.beneficiary_name || purchase.organizer_name,
          date: new Date(purchase.target_date),
          isRecurring: false,
          originalData: purchase,
          role: purchaseRoles.get(purchase.id),
        });
      }
    });
    
    // Sort by date
    events.sort((a, b) => differenceInDays(a.date, new Date()) - differenceInDays(b.date, new Date()));
    
    // Filter out past one-time events
    const filtered = events.filter(e => {
      const daysUntil = differenceInDays(e.date, new Date());
      if (!e.isRecurring && daysUntil < 0) return false;
      return true;
    });
    
    setUnifiedEvents(filtered);
  }, [reminders, groupPurchases, purchaseRoles, giftRoles]);

  const fetchAllEvents = async () => {
    setLoading(true);
    try {
      const newGiftRoles = new Map<string, 'organizer' | 'admin' | 'participant'>();
      const newPurchaseRoles = new Map<string, 'organizer' | 'admin' | 'participant'>();
      
      // Fetch gift reminders where user is creator OR invited friend
      if (customerEmail) {
        const emailLower = customerEmail.toLowerCase();
        
        // Get reminders user created
        const { data: createdReminders, error: createdError } = await supabase
          .from("gift_reminders")
          .select("*")
          .eq("customer_email", emailLower)
          .order("event_date", { ascending: true });
        
        if (createdError) console.error("Error fetching created reminders:", createdError);
        
        // Mark created reminders as organizer
        createdReminders?.forEach(r => newGiftRoles.set(r.id, 'organizer'));

        // Get reminders where user is invited (reminder_email or invited_friend_email)
        const { data: invitedReminders1, error: invitedError1 } = await supabase
          .from("gift_reminders")
          .select("*")
          .eq("reminder_email", emailLower)
          .or(`invited_friend_status.is.null,invited_friend_status.eq.active`)
          .order("event_date", { ascending: true });
        
        if (invitedError1) console.error("Error fetching invited reminders (reminder_email):", invitedError1);

        const { data: invitedReminders2, error: invitedError2 } = await supabase
          .from("gift_reminders")
          .select("*")
          .eq("invited_friend_email", emailLower)
          .or(`invited_friend_status.is.null,invited_friend_status.eq.active`)
          .order("event_date", { ascending: true });
        
        if (invitedError2) console.error("Error fetching invited reminders (invited_friend_email):", invitedError2);
        
        // Also fetch event_invitees to check for admin role
        const { data: inviteeRecords } = await supabase
          .from("event_invitees")
          .select("reminder_id, role, status")
          .eq("email", emailLower)
          .eq("status", "active");
        
        // Create a map of reminder_id -> role from event_invitees
        const inviteeRolesMap = new Map<string, string>();
        inviteeRecords?.forEach((inv: any) => {
          inviteeRolesMap.set(inv.reminder_id, inv.role);
        });
        
        // Mark invited reminders as participant or admin (unless already marked as organizer)
        [...(invitedReminders1 || []), ...(invitedReminders2 || [])].forEach(r => {
          if (!newGiftRoles.has(r.id)) {
            const inviteeRole = inviteeRolesMap.get(r.id);
            if (inviteeRole === 'admin') {
              newGiftRoles.set(r.id, 'admin');
            } else {
              newGiftRoles.set(r.id, 'participant');
            }
          }
        });

        // Combine and deduplicate reminders
        const allReminders = [
          ...(createdReminders || []),
          ...(invitedReminders1 || []),
          ...(invitedReminders2 || [])
        ];
        const uniqueReminders = allReminders.filter((r, index, self) => 
          index === self.findIndex(t => t.id === r.id)
        );
        
        setReminders(uniqueReminders);
        setGiftRoles(newGiftRoles);
      } else if (customerPhone) {
        const { data: reminderData, error: reminderError } = await supabase
          .from("gift_reminders")
          .select("*")
          .eq("customer_phone", customerPhone)
          .order("event_date", { ascending: true });
        
        if (reminderError) throw reminderError;
        // Phone-based users are organizers of their reminders
        reminderData?.forEach(r => newGiftRoles.set(r.id, 'organizer'));
        setReminders(reminderData || []);
        setGiftRoles(newGiftRoles);
      }

      // Fetch group purchases where user is organizer or participant
      if (customerEmail) {
        const emailLower = customerEmail.toLowerCase();
        
        // Purchases where user is organizer
        const { data: organizerPurchases, error: orgError } = await supabase
          .from("group_purchases")
          .select("*")
          .eq("organizer_email", emailLower)
          .eq("status", "active");
        
        if (orgError) console.error("Error fetching organizer purchases:", orgError);
        
        // Mark organizer purchases
        organizerPurchases?.forEach(p => newPurchaseRoles.set(p.id, 'organizer'));

        // Purchases where user is active participant (with role)
        const { data: participantData, error: partError } = await supabase
          .from("group_purchase_participants")
          .select("purchase_id, role")
          .eq("email", emailLower)
          .eq("status", "active");
        
        if (partError) console.error("Error fetching participant purchases:", partError);

        let participantPurchases: GroupPurchase[] = [];
        if (participantData && participantData.length > 0) {
          const purchaseIds = participantData.map(p => p.purchase_id);
          const { data: purchases, error: purchasesError } = await supabase
            .from("group_purchases")
            .select("*")
            .in("id", purchaseIds)
            .eq("status", "active");
          
          if (!purchasesError && purchases) {
            participantPurchases = purchases;
            
            // Set roles for participant purchases (unless already organizer)
            participantData.forEach(pd => {
              if (!newPurchaseRoles.has(pd.purchase_id)) {
                const role = pd.role === 'admin' ? 'admin' : 'participant';
                newPurchaseRoles.set(pd.purchase_id, role);
              }
            });
          }
        }

        // Combine and deduplicate
        const allPurchases = [...(organizerPurchases || []), ...participantPurchases];
        const uniquePurchases = allPurchases.filter((p, index, self) => 
          index === self.findIndex(t => t.id === p.id)
        );
        
        setGroupPurchases(uniquePurchases);
        setPurchaseRoles(newPurchaseRoles);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  // Keep fetchReminders for backward compatibility with form saves
  const fetchReminders = async () => {
    await fetchAllEvents();
  };

  const resetForm = () => {
    setDate(undefined);
    setRecipientName("");
    setMessage("");
    setReminderDays("7");
    setIsRecurring(true);
    setSendEmailReminder(true);
    setReminderPhone("");
    setPhoneCountryCode("+91");
    setOccasion("");
    setCustomOccasion("");
    setEditingId(null);
    setInviteFriend({ email: "", phone: "", phoneCountryCode: "+91" });
    setInvitedPeople([]);
    setTouched({});
    setFieldErrors({});
    setInviteEmailError("");
    setInviteLinkEnabled(false);
    setInviteLinkApprovalMode("auto");
    setMaxSelfInvites(null);
  };

  const addPersonToInvite = () => {
    const email = inviteFriend.email.trim();
    if (!email) {
      setInviteEmailError("Please enter an email address");
      return;
    }
    
    // Comprehensive email validation
    const validation = validateEmail(email);
    if (!validation.valid) {
      setInviteEmailError(validation.error || "Invalid email address");
      return;
    }
    
    // Check for duplicates
    if (invitedPeople.some(p => p.email.toLowerCase() === email.toLowerCase())) {
      setInviteEmailError("This person is already added");
      return;
    }
    
    setInvitedPeople(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        email: email,
        phone: inviteFriend.phone.trim(),
        phoneCountryCode: inviteFriend.phoneCountryCode,
      }
    ]);
    setInviteFriend({ email: "", phone: "", phoneCountryCode: "+91" });
    setInviteEmailError("");
  };

  const removePersonFromInvite = (id: string) => {
    setInvitedPeople(prev => prev.filter(p => p.id !== id));
  };

  const attemptDialogClose = () => {
    if (hasUnsavedChanges && !editingId) {
      setShowExitWarning(true);
    } else {
      setDialogOpen(false);
      resetForm();
      onDialogClose?.();
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && hasUnsavedChanges && !editingId) {
      setShowExitWarning(true);
      return;
    }
    setDialogOpen(open);
    if (!open) {
      resetForm();
      onDialogClose?.();
    }
  };

  const openEditDialog = (reminder: GiftReminder) => {
    setEditingId(reminder.id);
    setDate(new Date(reminder.event_date));
    setRecipientName(reminder.recipient_name || "");
    setMessage(reminder.message || "");
    setReminderDays(reminder.reminder_days_before.toString());
    setIsRecurring(reminder.is_recurring);
    setSendEmailReminder(!!reminder.reminder_email);
    setOccasion(reminder.occasion || "");
    // Check if occasion is a custom one (not in predefined list)
    const isCustomOccasion = reminder.occasion && !OCCASIONS.some(o => o.value === reminder.occasion);
    if (isCustomOccasion) {
      setOccasion("other");
      setCustomOccasion(reminder.occasion || "");
    }
    if (reminder.reminder_phone) {
      // Extract country code
      const phone = reminder.reminder_phone;
      const foundCode = COUNTRY_CODES.find(c => phone.startsWith(c.code));
      if (foundCode) {
        setPhoneCountryCode(foundCode.code);
        setReminderPhone(phone.replace(foundCode.code, ""));
      } else {
        setReminderPhone(phone);
      }
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validate all required fields
    const newTouched = { occasion: true, recipientName: true, date: true };
    setTouched(prev => ({ ...prev, ...newTouched }));
    
    const occasionError = validateReminderField('occasion', occasion);
    const recipientError = validateReminderField('recipientName', recipientName);
    const dateError = validateReminderField('date', date);
    
    setFieldErrors({ 
      occasion: occasionError, 
      recipientName: recipientError, 
      date: dateError 
    });
    
    if (occasionError || recipientError || dateError) {
      return;
    }
    
    // Generate event name from occasion and recipient
    const occasionLabel = occasion === "other" 
      ? (customOccasion.trim() || "Event")
      : (OCCASIONS.find(o => o.value === occasion)?.label.replace(/^[^\s]+\s/, '') || occasion);
    const generatedEventName = `${recipientName.trim()}'s ${occasionLabel}`;

    setSaving(true);

    try {
      const fullPhone = reminderPhone.trim() ? `${phoneCountryCode}${reminderPhone.replace(/^0+/, '')}` : null;
      
      // Combine current input with already added people
      const allInvitees = [...invitedPeople];
      if (inviteFriend.email.trim()) {
        allInvitees.push({
          id: crypto.randomUUID(),
          email: inviteFriend.email.trim(),
          phone: inviteFriend.phone.trim(),
          phoneCountryCode: inviteFriend.phoneCountryCode,
        });
      }
      
      // First invited person goes into the main reminder record
      const firstInvitee = allInvitees[0];
      const invitedFriendPhone = firstInvitee?.phone 
        ? `${firstInvitee.phoneCountryCode}${firstInvitee.phone.replace(/^0+/, '')}` 
        : null;
      
      const reminderData = {
        customer_email: customerEmail?.toLowerCase() || null,
        customer_phone: customerPhone || null,
        event_name: generatedEventName,
        recipient_name: recipientName.trim() || null,
        event_date: format(date, "yyyy-MM-dd"),
        message: message.trim() || null,
        reminder_days_before: parseInt(reminderDays),
        is_recurring: isRecurring,
        reminder_email: sendEmailReminder ? (customerEmail?.toLowerCase() || null) : null,
        reminder_phone: fullPhone,
        occasion: occasion === "other" ? (customOccasion.trim() || "other") : (occasion || null),
        invited_friend_email: firstInvitee?.email || null,
        invited_friend_phone: invitedFriendPhone,
        invite_link_enabled: inviteLinkEnabled,
        invite_link_approval_mode: inviteLinkApprovalMode,
        max_self_invites: maxSelfInvites,
      };

      let reminderId = editingId;

      if (editingId) {
        const { error } = await supabase
          .from("gift_reminders")
          .update(reminderData)
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Reminder updated!");
      } else {
        const { data, error } = await supabase
          .from("gift_reminders")
          .insert(reminderData)
          .select('id')
          .single();

        if (error) throw error;
        reminderId = data?.id;
        toast.success("Reminder added! We'll remind you before the event 🎉");
      }

      // Add additional invitees to event_invitees table
      if (reminderId && allInvitees.length > 1) {
        const additionalInvitees = allInvitees.slice(1).map(person => ({
          reminder_id: reminderId,
          email: person.email.toLowerCase(),
          phone: person.phone ? `${person.phoneCountryCode}${person.phone.replace(/^0+/, '')}` : null,
          invited_by: customerEmail?.toLowerCase() || 'organizer',
        }));
        
        await supabase.from("event_invitees").insert(additionalInvitees);
      }

      // Send invitation emails - always send organizer email, plus invitees if any
      if (reminderId) {
        try {
          await supabase.functions.invoke("send-event-invitation", {
            body: { reminderId, type: 'initial' }
          });
        } catch (emailError) {
          console.error("Failed to send invitation emails:", emailError);
        }
      }

      handleDialogClose(false);
      fetchReminders();
    } catch (error) {
      console.error("Error saving reminder:", error);
      toast.error("Failed to save reminder");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("gift_reminders")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Reminder deleted");
      setReminders(reminders.filter(r => r.id !== id));
    } catch (error) {
      console.error("Error deleting reminder:", error);
      toast.error("Failed to delete reminder");
    }
  };

  const getNextOccurrence = (reminder: GiftReminder) => {
    const eventDate = new Date(reminder.event_date);
    const today = new Date();
    
    if (reminder.is_recurring) {
      let nextDate = new Date(today.getFullYear(), eventDate.getMonth(), eventDate.getDate());
      if (isBefore(nextDate, today)) {
        nextDate = addYears(nextDate, 1);
      }
      return nextDate;
    }
    return eventDate;
  };

  const getDaysUntil = (reminder: GiftReminder) => {
    const nextDate = getNextOccurrence(reminder);
    return differenceInDays(nextDate, new Date());
  };

  const getStatusBadge = (reminder: GiftReminder) => {
    const daysUntil = getDaysUntil(reminder);
    
    // Check if one-time event has passed
    if (!reminder.is_recurring && daysUntil < 0) {
      return <Badge variant="secondary" className="text-xs">Passed</Badge>;
    }
    
    if (daysUntil <= 7) {
      return <Badge variant="destructive" className="text-xs">In {daysUntil}d</Badge>;
    } else if (daysUntil <= 30) {
      return <Badge variant="outline" className="text-xs border-customer text-customer">In {daysUntil}d</Badge>;
    } else {
      return <Badge variant="secondary" className="text-xs">In {daysUntil}d</Badge>;
    }
  };

  if (loading && !dialogOpenProp) {
    return (
      <Card className="border-customer/20">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If dialogOpenProp is true (dialog is open), only render the dialog (no card wrapper)
  // This is used for the "quick add" functionality where we don't want to show another card
  if (dialogOpenProp === true) {
    return (
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Reminder" : "Set a Reminder"}</DialogTitle>
            <DialogDescription>
              We'll remind you before the event so you can organize a group purchase!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Occasion Type
                <span className="text-destructive">*</span>
              </Label>
              <Select 
                value={occasion} 
                onValueChange={(val) => { 
                  setOccasion(val); 
                  if (val !== "other") setCustomOccasion(""); 
                  if (touched.occasion) {
                    setFieldErrors(prev => ({ ...prev, occasion: val ? '' : 'Please select an occasion type' }));
                  }
                }}
              >
                <SelectTrigger 
                  className={cn(touched.occasion && fieldErrors.occasion && "border-destructive")}
                  onBlur={() => handleReminderFieldBlur('occasion', occasion)}
                >
                  <SelectValue placeholder="Select occasion type" />
                </SelectTrigger>
                <SelectContent>
                  {OCCASIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={touched.occasion ? fieldErrors.occasion : undefined} />
              {occasion === "other" && (
                <Input
                  placeholder="Enter occasion name"
                  value={customOccasion}
                  onChange={(e) => setCustomOccasion(e.target.value)}
                  className="mt-2"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipientName" className="flex items-center gap-1">
                Who is it for?
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="recipientName"
                placeholder="e.g., John, Mom & Dad"
                value={recipientName}
                onChange={(e) => {
                  setRecipientName(e.target.value);
                  if (touched.recipientName) {
                    setFieldErrors(prev => ({ ...prev, recipientName: e.target.value.trim() ? '' : 'Please enter who this is for' }));
                  }
                }}
                onBlur={() => handleReminderFieldBlur('recipientName', recipientName)}
                className={cn(touched.recipientName && fieldErrors.recipientName && "border-destructive")}
              />
              <FieldError error={touched.recipientName ? fieldErrors.recipientName : undefined} />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                Event Date
                <span className="text-destructive">*</span>
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !date && "text-muted-foreground",
                      touched.date && fieldErrors.date && "border-destructive"
                    )}
                    onBlur={() => handleReminderFieldBlur('date', date)}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setCalendarOpen(false);
                      if (touched.date) {
                        setFieldErrors(prev => ({ ...prev, date: d ? '' : 'Please select a date' }));
                      }
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <FieldError error={touched.date ? fieldErrors.date : undefined} />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="recurring" className="text-sm">Repeat every year</Label>
              <Switch id="recurring" checked={isRecurring} onCheckedChange={setIsRecurring} />
            </div>

            <div className="space-y-2">
              <Label>Remind me before</Label>
              <Select value={reminderDays} onValueChange={setReminderDays}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days before</SelectItem>
                  <SelectItem value="7">1 week before</SelectItem>
                  <SelectItem value="14">2 weeks before</SelectItem>
                  <SelectItem value="30">1 month before</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="sendEmailReminder" 
                checked={sendEmailReminder}
                onCheckedChange={(checked) => setSendEmailReminder(checked === true)}
              />
              <Label htmlFor="sendEmailReminder" className="text-sm cursor-pointer">
                Send me email reminders {customerEmail && <span className="text-muted-foreground">({customerEmail})</span>}
              </Label>
            </div>

            {/* Invite Friends */}
            <div className="space-y-3 p-3 bg-customer/5 rounded-lg border border-customer/10">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-customer" />
                <Label className="text-sm font-medium">Invite Friends to This Event</Label>
              </div>
              <p className="text-xs text-muted-foreground">Organize a group gift! Add friends who'll contribute.</p>
              
              {/* Already added people - shown as chips */}
              {invitedPeople.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-background rounded-md border">
                  {invitedPeople.map((person) => (
                    <div key={person.id} className="flex items-center gap-1 bg-customer/15 text-customer px-2.5 py-1 rounded-full text-xs font-medium">
                      <span>{person.email}</span>
                      <button
                        type="button"
                        onClick={() => removePersonFromInvite(person.id)}
                        className="ml-1 hover:text-destructive transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Input row for adding a friend */}
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Friend's email"
                      value={inviteFriend.email}
                      onChange={(e) => handleInviteEmailChange(e.target.value)}
                      onBlur={handleInviteEmailBlur}
                      className={cn("flex-1 h-9", inviteEmailError && "border-destructive")}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addPersonToInvite}
                      disabled={!inviteFriend.email.trim()}
                      className="h-9 px-3 gap-1.5"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span className="hidden sm:inline">Add</span>
                    </Button>
                  </div>
                  <FieldError error={inviteEmailError} />
                </div>
                <div className="flex gap-2">
                  <Select 
                    value={inviteFriend.phoneCountryCode} 
                    onValueChange={(v) => setInviteFriend(prev => ({ ...prev, phoneCountryCode: v }))}
                  >
                    <SelectTrigger className="w-20 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    placeholder="Phone (optional)"
                    value={inviteFriend.phone}
                    onChange={(e) => setInviteFriend(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }))}
                    className="flex-1 h-9"
                  />
                </div>
                {invitedPeople.length > 0 && (
                  <p className="text-xs text-muted-foreground text-center">
                    {invitedPeople.length} friend{invitedPeople.length > 1 ? 's' : ''} added
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={attemptDialogClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-customer hover:bg-customer/90 text-customer-foreground">
              {saving ? "Saving..." : "Add Reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>

        {/* Unsaved Changes Warning Dialog */}
        <AlertDialog open={showExitWarning} onOpenChange={setShowExitWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-center">Discard Changes?</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                You have unsaved changes. Are you sure you want to close? Your progress will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="sm:justify-center gap-2">
              <AlertDialogCancel onClick={() => setShowExitWarning(false)}>
                Keep Editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowExitWarning(false);
                  setDialogOpen(false);
                  resetForm();
                  onDialogClose?.();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Discard Changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Dialog>
    );
  }

  return (
    <Card className="border-customer/30 bg-gradient-to-br from-pink-100/70 via-violet-100/50 to-emerald-100/60 dark:from-pink-950/30 dark:via-violet-950/25 dark:to-emerald-950/30 backdrop-blur-sm shadow-sm">
      <CardHeader className="pb-1 pt-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-1.5 text-sm font-semibold">
            <CalendarIcon className="h-4 w-4 text-customer" />
            Upcoming Group Events
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Reminder" : "Set a Reminder"}</DialogTitle>
                <DialogDescription>
                  We'll remind you before the event so you can organize a group purchase!
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Occasion Type
                    <span className="text-destructive">*</span>
                  </Label>
                  <Select 
                    value={occasion} 
                    onValueChange={(val) => { 
                      setOccasion(val); 
                      if (val !== "other") setCustomOccasion("");
                      if (touched.occasion) {
                        setFieldErrors(prev => ({ ...prev, occasion: val ? '' : 'Please select an occasion type' }));
                      }
                    }}
                  >
                    <SelectTrigger 
                      className={cn(touched.occasion && fieldErrors.occasion && "border-destructive")}
                      onBlur={() => handleReminderFieldBlur('occasion', occasion)}
                    >
                      <SelectValue placeholder="Select occasion type" />
                    </SelectTrigger>
                    <SelectContent>
                      {OCCASIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError error={touched.occasion ? fieldErrors.occasion : undefined} />
                  {occasion === "other" && (
                    <Input
                      placeholder="Enter occasion name"
                      value={customOccasion}
                      onChange={(e) => setCustomOccasion(e.target.value)}
                      className="mt-2"
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="recipientName2" className="flex items-center gap-1">
                    Who is it for?
                    <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="recipientName2"
                    placeholder="e.g., John, Mom & Dad"
                    value={recipientName}
                    onChange={(e) => {
                      setRecipientName(e.target.value);
                      if (touched.recipientName) {
                        setFieldErrors(prev => ({ ...prev, recipientName: e.target.value.trim() ? '' : 'Please enter who this is for' }));
                      }
                    }}
                    onBlur={() => handleReminderFieldBlur('recipientName', recipientName)}
                    className={cn(touched.recipientName && fieldErrors.recipientName && "border-destructive")}
                  />
                  <FieldError error={touched.recipientName ? fieldErrors.recipientName : undefined} />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Event Date
                    <span className="text-destructive">*</span>
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !date && "text-muted-foreground",
                          touched.date && fieldErrors.date && "border-destructive"
                        )}
                        onBlur={() => handleReminderFieldBlur('date', date)}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => {
                          setDate(d);
                          if (touched.date) {
                            setFieldErrors(prev => ({ ...prev, date: d ? '' : 'Please select a date' }));
                          }
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                  <FieldError error={touched.date ? fieldErrors.date : undefined} />
                </div>

                {/* Recurring Toggle */}
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {isRecurring ? (
                      <RefreshCw className="h-4 w-4 text-customer" />
                    ) : (
                      <CalendarX className="h-4 w-4 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{isRecurring ? "Recurring Event" : "One-time Event"}</p>
                      <p className="text-xs text-muted-foreground">
                        {isRecurring ? "Reminds you every year" : "Only reminds once"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Remind Me</Label>
                  <Select value={reminderDays} onValueChange={setReminderDays}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timing" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days before</SelectItem>
                      <SelectItem value="7">7 days before</SelectItem>
                      <SelectItem value="14">14 days before</SelectItem>
                      <SelectItem value="30">30 days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Invite Friends */}
                <div className="space-y-3 p-3 bg-customer/5 rounded-lg border border-customer/10">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-customer" />
                    <Label className="text-sm font-medium">Invite Friends to This Event</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">Organize a group gift! Add friends who'll contribute.</p>
                  
                  {/* Already added people - shown as chips */}
                  {invitedPeople.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 bg-background rounded-md border">
                      {invitedPeople.map((person) => (
                        <div key={person.id} className="flex items-center gap-1 bg-customer/15 text-customer px-2.5 py-1 rounded-full text-xs font-medium">
                          <span>{person.email}</span>
                          <button
                            type="button"
                            onClick={() => removePersonFromInvite(person.id)}
                            className="ml-1 hover:text-destructive transition-colors"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Input row for adding a friend */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Friend's email"
                        value={inviteFriend.email}
                        onChange={(e) => setInviteFriend(prev => ({ ...prev, email: e.target.value }))}
                        className="flex-1 h-9"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addPersonToInvite}
                        disabled={!inviteFriend.email.trim()}
                        className="h-9 px-3 gap-1.5"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={inviteFriend.phoneCountryCode} 
                        onValueChange={(v) => setInviteFriend(prev => ({ ...prev, phoneCountryCode: v }))}
                      >
                        <SelectTrigger className="w-20 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={inviteFriend.phone}
                        onChange={(e) => setInviteFriend(prev => ({ ...prev, phone: e.target.value.replace(/\D/g, '') }))}
                        className="flex-1 h-9"
                      />
                    </div>
                    {invitedPeople.length > 0 && (
                      <p className="text-xs text-muted-foreground text-center">
                        {invitedPeople.length} friend{invitedPeople.length > 1 ? 's' : ''} added
                      </p>
                    )}
                  </div>

                  {/* Shareable Invite Link Settings */}
                  <InviteLinkSettings
                    enabled={inviteLinkEnabled}
                    onEnabledChange={setInviteLinkEnabled}
                    approvalMode={inviteLinkApprovalMode}
                    onApprovalModeChange={setInviteLinkApprovalMode}
                    maxSelfInvites={maxSelfInvites}
                    onMaxSelfInvitesChange={setMaxSelfInvites}
                    colorScheme="customer"
                  />
                </div>

                {/* Reminder Contact - Optional */}
                <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="sendEmailReminder2" 
                      checked={sendEmailReminder}
                      onCheckedChange={(checked) => setSendEmailReminder(checked === true)}
                    />
                    <Label htmlFor="sendEmailReminder2" className="text-sm cursor-pointer">
                      Send me email reminders {customerEmail && <span className="text-muted-foreground">({customerEmail})</span>}
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                      <SelectTrigger className="w-20 h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.flag} {c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="Your phone number"
                      value={reminderPhone}
                      onChange={(e) => setReminderPhone(e.target.value.replace(/\D/g, ''))}
                      className="flex-1 h-9"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Add phone for SMS reminders</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Personal Note (optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Gift ideas, budget suggestions, preferences..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => handleDialogClose(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-customer hover:bg-customer/90 text-customer-foreground">
                  {saving ? "Saving..." : editingId ? "Update" : "Add Reminder"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {!compact && (
          <CardDescription className="text-xs">
            Gifts, purchases & payments all in one place
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-1 pb-3">
        {unifiedEvents.length === 0 ? (
          <div className="text-center py-3 text-muted-foreground">
            <Gift className="h-6 w-6 mx-auto mb-1.5 opacity-50 text-customer" />
            <p className="text-sm">No upcoming events</p>
            <p className="text-xs">Add birthdays, purchases & more!</p>
          </div>
        ) : (
          <div className={cn(
            "space-y-1.5",
            compact && unifiedEvents.length > 3 && "max-h-[280px] overflow-y-auto pr-1"
          )}>
            {unifiedEvents.map((event) => {
                const config = CATEGORY_CONFIG[event.category];
                const daysUntil = differenceInDays(event.date, new Date());
                const originalReminder = event.category === 'gift' ? event.originalData as GiftReminder : null;
                
                return (
                  <div
                    key={`${event.category}-${event.id}`}
                    className={cn(
                      "flex items-center justify-between p-2 rounded-lg group hover:opacity-90 transition-all cursor-pointer border-l-3",
                      config.bgColor,
                      event.category === 'gift' && "border-l-gift",
                      event.category === 'purchase' && "border-l-purchase",
                      event.category === 'payment' && "border-l-payment"
                    )}
                    onClick={() => {
                      if (event.category === 'gift') {
                        window.location.href = `/events/${event.id}`;
                      } else {
                        // Both 'purchase' and 'payment' route to /purchases/:id
                        window.location.href = `/purchases/${event.id}`;
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Category Badge */}
                        <Badge 
                          variant="outline" 
                          className={cn("text-[10px] px-1.5 py-0 h-4 border-0", config.bgColor, config.color)}
                        >
                          <span className="mr-1">{config.icon}</span>
                          {config.label}
                        </Badge>
                        
                        {/* Occasion emoji for gifts */}
                        {event.category === 'gift' && originalReminder?.occasion && (
                          <span className="text-sm">
                            {OCCASIONS.find(o => o.value === originalReminder.occasion)?.label.split(' ')[0] || '✨'}
                          </span>
                        )}
                        
                        {/* Days until badge */}
                        {daysUntil <= 7 ? (
                          <Badge variant="destructive" className="text-xs">In {daysUntil}d</Badge>
                        ) : daysUntil <= 30 ? (
                          <Badge variant="outline" className={cn("text-xs border-0", config.bgColor, config.color)}>In {daysUntil}d</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">In {daysUntil}d</Badge>
                        )}
                        
                        {/* One-time indicator */}
                        {!event.isRecurring && (
                          <CalendarX className="h-3 w-3 text-muted-foreground" />
                        )}
                      </div>
                      
                      {/* Event Title & Recipient */}
                      <div className="mt-1">
                        <p className="text-sm font-medium truncate">{event.title}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          {format(event.date, "MMM d, yyyy")}
                          {event.recipient && (
                            <>
                              <span className="mx-1">•</span>
                              <span className="font-medium">{event.recipient}</span>
                            </>
                          )}
                          <MessageSquare className={cn("h-3 w-3 ml-1", config.color)} />
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1.5">
                      {/* Role Badge */}
                      {event.role === 'organizer' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 h-4 bg-purple-500/10 text-purple-700 border-0 gap-0.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Crown className="h-2.5 w-2.5" />
                          Organizer
                        </Badge>
                      )}
                      {event.role === 'admin' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 h-4 bg-purple-500/10 text-purple-700 border-0 gap-0.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Shield className="h-2.5 w-2.5" />
                          Admin
                        </Badge>
                      )}
                      {event.role === 'participant' && (
                        <Badge 
                          variant="outline" 
                          className="text-[10px] px-1.5 py-0 h-4 bg-muted text-muted-foreground border-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Participant
                        </Badge>
                      )}
                      <ExternalLink className={cn("h-3 w-3 opacity-50 group-hover:opacity-100", config.color)} />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
